import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Sparkles, Layers, Tags, Package, Users, ShoppingBag, Boxes, ScanBarcode,
  HandCoins, Wallet, Receipt, LayoutDashboard, UserCog, Settings as SettingsIcon, Rocket,
  ChevronLeft, ChevronRight, ArrowRight, CheckCircle2,
} from "lucide-react";

const STEPS = [
  { icon: Layers, to: "/activities", menu: "Activités", mod: null, title: "Choisissez l'activité en haut",
    text: "En haut à gauche, un sélecteur permet de basculer entre « Vue globale » (situation consolidée) et chacune de vos activités. Le menu et les données s'adaptent à l'activité choisie." },
  { icon: Layers, to: "/activities", menu: "Activités", mod: "__admin", title: "Créez votre activité",
    text: "Une activité = un commerce ou un métier (boutique, matériaux, location…). Choisissez le type : cela pré-active les bons menus et des catégories de départ. Sélectionnez ensuite l'activité en haut à gauche pour travailler dedans." },
  { icon: Tags, to: "/categories", menu: "Catégories", mod: "products", title: "Organisez vos catégories",
    text: "Une catégorie regroupe des produits similaires (Boissons, Ciment, Sable…) et porte ses unités de mesure (ex. Boissons → bouteille, pack de 6, casier de 24). Modifiez ou ajoutez les vôtres." },
  { icon: Package, to: "/products", menu: "Catalogue", mod: "products", title: "Ajoutez vos produits",
    text: "Renseignez le prix à l'unité (ex. la bouteille) puis, si besoin, la « vente par lot » (1 pack = 6 bouteilles à tel prix). Le code-barres est généré automatiquement (ART-…) — cliquez sur l'icône code-barres pour imprimer l'étiquette." },
  { icon: Users, to: "/parties", menu: "Clients & Fournisseurs", mod: "parties", title: "Enregistrez clients et fournisseurs",
    text: "Choisissez Client ou Fournisseur : chacun reçoit un code d'identification (CLI-… / FRN-…). Le plafond de crédit sert pour les ventes à crédit." },
  { icon: ShoppingBag, to: "/purchases", menu: "Achats", mod: "purchases", title: "Constituez votre stock",
    text: "Passez une commande fournisseur puis « Réceptionner » : le stock augmente automatiquement. (Ou Menu Stock → entrée manuelle / inventaire.) C'est ainsi que les quantités se remplissent." },
  { icon: Boxes, to: "/stock", menu: "Stock", mod: "stock", title: "Gérez le stock, inventaires et transferts",
    text: "Entrées et sorties manuelles, inventaires, pertes/casses et transferts entre points de vente. Le stock reste toujours à jour par emplacement." },
  { icon: Wallet, to: "/cash", menu: "Caisse", mod: "cash", title: "Ouvrez la caisse",
    text: "Avant de vendre, ouvrez la caisse en début de journée ; fermez-la en fin de journée : l'écart éventuel est calculé automatiquement. Chaque vendeur ne voit que sa propre caisse." },
  { icon: ScanBarcode, to: "/pos", menu: "Ventes (POS)", mod: "pos", title: "Vendez au comptoir",
    text: "Ajoutez les produits (à l'unité ou au lot), choisissez Comptant, Partiel ou Crédit, encaissez. Après la vente, un aperçu du ticket s'affiche (impression + WhatsApp). Fonctionne aussi hors connexion." },
  { icon: HandCoins, to: "/receivables", menu: "Créances", mod: "receivables", title: "Suivez et encaissez les crédits",
    text: "Les ventes à crédit apparaissent ici avec le solde dû par client. Encaissez tout ou partie, envoyez une relance WhatsApp." },
  { icon: Receipt, to: "/expenses", menu: "Dépenses", mod: "expenses", title: "Enregistrez les dépenses",
    text: "Notez chaque sortie d'argent (achats divers, transport, salaires…) pour un résultat net juste." },
  { icon: LayoutDashboard, to: "/", menu: "Tableau de bord", mod: null, title: "Pilotez votre résultat",
    text: "Vue consolidée : ventes du jour, encaissements, dépenses, créances dues et bénéfice net par activité." },
  { icon: UserCog, to: "/users", menu: "Utilisateurs", mod: "__admin", title: "Ajoutez vos employés",
    text: "Invitez vos employés (caissier, magasinier, comptable…). Chacun reçoit un e-mail pour définir son mot de passe. Réglez leur rôle et les modules autorisés." },
  { icon: SettingsIcon, to: "/settings", menu: "Paramètres", mod: "__admin", title: "Personnalisez votre entreprise",
    text: "Nom, logo, devise, adresse, téléphone, RCCM/NIF… ces informations apparaissent sur vos factures PDF." },
];

const STORE_KEY = "biashara_guide_step";

export default function Guide() {
  const [acts, setActs] = useState([]);
  const { user } = useAuth();
  useEffect(() => { api.get("/activities").then((r) => setActs(r.data.filter((a) => a.status !== "archived"))).catch(() => {}); }, []);

  const isAdmin = !!(user && (user.is_owner || ["owner", "admin"].includes(user.role)));
  const mods = user?.allowed_modules;
  const canSee = (s) => {
    if (s.mod === null) return true;
    if (s.mod === "__admin") return isAdmin;
    if (isAdmin || !mods) return true;
    return mods.includes(s.mod);
  };
  const visibleSteps = STEPS.filter(canSee);
  const total = visibleSteps.length;

  const [idx, setIdx] = useState(() => {
    const saved = parseInt(localStorage.getItem(STORE_KEY) || "0", 10);
    return Number.isFinite(saved) ? saved : 0;
  });
  const cur = Math.min(idx, Math.max(0, total - 1));
  useEffect(() => { try { localStorage.setItem(STORE_KEY, String(cur)); } catch (e) { /* ignore */ } }, [cur]);

  if (total === 0) return <div className="text-[#64748B]">Aucune étape disponible pour votre profil.</div>;

  const s = visibleSteps[cur];
  const Icon = s.icon;
  const isFirst = cur === 0;
  const isLast = cur === total - 1;
  const pct = Math.round(((cur + 1) / total) * 100);

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Hero */}
      <div className="rounded-2xl p-6 text-white" style={{ background: "linear-gradient(160deg,#1A3A5C 0%,#16324F 55%,#0E2135 100%)" }}>
        <div className="flex items-center gap-2 text-sm opacity-90"><Rocket className="w-4 h-4" /> Guide de démarrage</div>
        <h1 className="text-2xl md:text-3xl font-bold font-head mt-2">Prenez votre espace en main, étape par étape</h1>
        <p className="text-sm opacity-90 mt-2 max-w-2xl">Suivez le parcours avec « Précédent » et « Suivant ». Ouvrez le menu concerné, faites la manipulation, revenez : le guide reprend automatiquement à la même étape.</p>
        {acts.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="guide-activities">
            {acts.map((a) => <span key={a.id} className="text-xs bg-white/15 px-2.5 py-1 rounded-full">{a.name}</span>)}
          </div>
        )}
      </div>

      {/* Progress */}
      <div>
        <div className="flex items-center justify-between text-xs font-semibold text-[#64748B] mb-1.5">
          <span data-testid="guide-step-indicator">Étape {cur + 1} sur {total}</span>
          <span>{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-[#EEF2F7] overflow-hidden">
          <div className="h-full rounded-full bg-[#2563EB] transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        {/* Clickable dots */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {visibleSteps.map((st, i) => (
            <button key={i} onClick={() => setIdx(i)} title={st.title} data-testid={`guide-dot-${i + 1}`}
              className={`w-7 h-7 rounded-lg text-[11px] font-bold transition-colors ${i === cur ? "bg-[#16324F] text-white" : i < cur ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#EEF2F7] text-[#94A3B8] hover:bg-[#E2E8F0]"}`}>
              {i < cur ? "✓" : i + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Current step card */}
      <div className="card p-6 animate-fade-up" key={cur} data-testid={`guide-step-${cur + 1}`}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#16324F] text-white flex items-center justify-center shrink-0"><Icon className="w-6 h-6" /></div>
          <div className="flex-1">
            <span className="inline-block text-xs font-medium text-[#2563EB] bg-[#EFF6FF] px-2 py-0.5 rounded-full mb-2">Menu : {s.menu}</span>
            <h2 className="text-xl font-bold font-head text-[#16324F]">{s.title}</h2>
            <p className="text-sm text-[#475569] mt-2 leading-relaxed">{s.text}</p>
            <Link to={s.to} className="inline-flex items-center gap-2 mt-4 bg-[#16A34A] text-white font-semibold px-4 py-2.5 rounded-xl hover:bg-[#15803D] transition-colors" data-testid="guide-open-menu-btn">
              Ouvrir « {s.menu} » <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={isFirst} data-testid="guide-prev-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-semibold border border-[#E2E8F0] text-[#334155] bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#F8FAFC] transition-colors">
          <ChevronLeft className="w-4 h-4" /> Précédent
        </button>
        {isLast ? (
          <Link to="/" onClick={() => { try { localStorage.removeItem(STORE_KEY); } catch (e) { /* ignore */ } }} data-testid="guide-finish-btn"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl font-semibold bg-[#16324F] text-white hover:bg-[#0F2438] transition-colors">
            <CheckCircle2 className="w-4 h-4" /> Terminer
          </Link>
        ) : (
          <button onClick={() => setIdx((i) => Math.min(total - 1, i + 1))} data-testid="guide-next-btn"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl font-semibold bg-[#2563EB] text-white hover:bg-[#1D4ED8] transition-colors">
            Suivant <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
